/*
 * authFetch — centralized API client for Command app.
 *
 * Auth transport:
 *   - Web browser: httpOnly `cmd_auth` cookie set by the server on
 *     login/signup. JS cannot read it, so an XSS cannot exfiltrate the
 *     JWT. The browser attaches it automatically on same-origin requests
 *     (and on cross-origin requests when credentials: "include" is used).
 *     We keep an in-memory _token only for this tab's session so the UI
 *     knows a login just happened; we do NOT persist it.
 *   - Capacitor native (iOS/Android): cookies are unreliable in the
 *     webview across cold starts, so native sticks with Bearer. The
 *     token is kept in Capacitor Preferences (never localStorage).
 *
 * Events:
 *   - auth:set      — fired when setAuthToken stores a new token
 *   - auth:expired  — fired on 401 so the app can bounce to login
 */

import { Preferences } from "@capacitor/preferences";
import { isNative } from "./native";

const TOKEN_KEY = "cmd_auth_token";

/* In-memory token. Source of truth for Bearer injection. */
let _token = null;

/* One-time cleanup: earlier builds mirrored the JWT into localStorage.
   Clear it on every boot so upgraded clients stop carrying the credential
   in a JS-readable slot. */
try { localStorage.removeItem(TOKEN_KEY); } catch {}

/* On native only, restore from Preferences on module load. Web has no
   JS-readable copy — the httpOnly cookie authenticates silently, and the
   first protected fetch (e.g. /api/list-workspaces) confirms the session. */
if (isNative()) {
  (async () => {
    try {
      const { value } = await Preferences.get({ key: TOKEN_KEY });
      if (value) {
        _token = value;
        if (typeof window !== "undefined") {
          try { window.dispatchEvent(new CustomEvent("auth:set", { detail: { token: value } })); } catch {}
        }
      }
    } catch {}
  })();
}

export function setAuthToken(token) {
  const prev = _token;
  _token = token;
  /* Persist on native only. On web, persistence is the httpOnly cookie
     set by the server — we intentionally do not touch localStorage. */
  if (isNative()) {
    try {
      if (token) Preferences.set({ key: TOKEN_KEY, value: token });
      else Preferences.remove({ key: TOKEN_KEY });
    } catch {}
  }
  if (typeof window !== "undefined" && token && token !== prev) {
    try { window.dispatchEvent(new CustomEvent("auth:set", { detail: { token } })); } catch {}
  }
}

export function clearAuthToken() {
  _token = null;
  if (isNative()) {
    try { Preferences.remove({ key: TOKEN_KEY }); } catch {}
  } else {
    /* Best-effort server logout so the httpOnly cookie is cleared.
       Fire-and-forget; UI proceeds immediately. */
    try {
      fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({ action: "logout" }),
      }).catch(() => {});
    } catch {}
  }
}

/* ── Core request function ───────────────────────────────── */
export default async function authFetch(url, options = {}) {
  const opts = { ...options };
  opts.headers = { ...opts.headers };
  /* Always send cookies so the httpOnly session cookie travels on web.
     Harmless on native (no cookies present). */
  if (opts.credentials === undefined) opts.credentials = "include";

  /* Bearer header for native and for the current tab's web session.
     On web reload, _token is empty and the cookie alone authenticates. */
  if (_token && !opts.headers.Authorization) {
    opts.headers.Authorization = `Bearer ${_token}`;
  }

  const res = await fetch(url, opts);

  /* Global 401 handling: clear token and notify the app to show login screen.
     Skip for /api/auth calls (login attempts that naturally return 401).
     Fire even if _token is already null — a missing token is exactly the
     case where the user needs to be bounced back to login. */
  if (res.status === 401 && !url.includes("/api/auth")) {
    if (_token) clearAuthToken();
    window.dispatchEvent(new CustomEvent("auth:expired"));
  }

  /* Global 403 handling: detect subscription expiry and notify the app */
  if (res.status === 403 && !url.includes("/api/auth")) {
    try {
      const cloned = res.clone();
      const body = await cloned.json();
      if (body.error === "subscription_expired") {
        window.dispatchEvent(new CustomEvent("subscription:expired", { detail: "subscription_expired" }));
      }
    } catch {}
  }

  return res;
}

/* ── Convenience helpers (match apiCall pattern in useRealtimeSync) ── */
export async function apiCall(method, url, body, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const opts = { method, headers: { "Content-Type": "application/json" }, keepalive: true };
      if (body) opts.body = JSON.stringify(body);
      const r = await authFetch(url, opts);
      if (!r.ok) {
        /* Always include HTTP status so downstream error handlers can
           distinguish 401 (auth) from 500 (server) from 404 (routing).
           HTTP/2 responses often omit statusText, so an empty reason
           phrase must not produce a blank Error message. Try JSON first,
           then raw text so Vercel function-crash responses (which may
           return HTML/empty bodies) still give us something useful. */
        const rawText = await r.text().catch(() => "");
        let parsed = null;
        try { parsed = JSON.parse(rawText); } catch {}
        const bodyMsg =
          parsed?.error ||
          parsed?.message ||
          (rawText && rawText.length < 200 ? rawText : rawText.slice(0, 200)) ||
          r.statusText ||
          "";
        const statusLabel = `HTTP ${r.status}`;
        const fullMsg = bodyMsg ? `${statusLabel}: ${bodyMsg}` : statusLabel;
        const error = new Error(fullMsg);
        error.status = r.status;
        error.body = parsed || rawText;
        throw error;
      }
      return await r.json();
    } catch (err) {
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 500));
        continue;
      }
      throw err;
    }
  }
}
